import { type WishlistItem, type WishStatus } from '@fin/shared';
import { useState } from 'react';
import { Button, ConfirmButton, ErrorBox, Field, Icon, Input, Loading, Modal, NumberInput, PageHead, Segmented, Select, cx, useAction } from '../../components/ui.tsx';
import { del, patch, post } from '../../lib/api.ts';
import { money, priorityLabels, rub } from '../../lib/format.ts';
import { invalidate, useQuery } from '../../lib/query.ts';

export function WishlistPage() {
  const q = useQuery<WishlistItem[]>('/wishlist');
  const [status, setStatus] = useState<WishStatus>('active');
  const [edit, setEdit] = useState<WishlistItem | 'new' | null>(null);
  if (q.error && !q.data) return <ErrorBox error={q.error} onRetry={q.reload} />;
  if (!q.data) return <Loading />;
  const items = q.data.filter((i) => i.status === status);
  const groups = [1, 2, 3, 4, 5].map((p) => ({ p, list: items.filter((i) => i.priority === p) })).filter((g) => g.list.length);
  const total = (list: WishlistItem[]) => list.reduce((s, i) => s + (i.price ?? 0), 0);

  return (
    <div className="page">
      <PageHead title="Покупки" sub="Что купить и насколько это нужно" actions={<Button variant="primary" onClick={() => setEdit('new')}><Icon name="plus" /> Добавить</Button>} />
      <Segmented label="Статус" value={status} onChange={setStatus} options={[
        { value: 'active', label: 'В планах' }, { value: 'bought', label: 'Куплено' }, { value: 'dropped', label: 'Передумал' },
      ]} />

      {status === 'active' ? (
        <div className="prio-summary">
          {[1, 2, 3, 4].map((p) => {
            const list = items.filter((i) => i.priority === p);
            return <div key={p} className={`prio-card prio-${p}`}><span>{priorityLabels[p]}</span><strong className="money">{rub(total(list))}</strong><span className="small">{list.length} поз.</span></div>;
          })}
        </div>
      ) : null}

      {groups.map((g) => (
        <section key={g.p} className="wish-group">
          <h2 className="wish-group-head"><span className={`prio-chip prio-${g.p}`}>{priorityLabels[g.p]}</span><span className="muted money">{rub(total(g.list))}</span></h2>
          <ul className="wishes">
            {g.list.map((i) => (
              <li key={i.id} className="wish">
                <button type="button" className="wish-main" onClick={() => setEdit(i)}>
                  <span className="wish-name">{i.name}</span>
                  {i.pros || i.cons ? (
                    <span className="wish-pc">
                      {i.pros ? <span className="pro">За: {i.pros}</span> : null}
                      {i.cons ? <span className="con">Против: {i.cons}</span> : null}
                    </span>
                  ) : null}
                </button>
                {i.url ? <a className="icon-btn" href={i.url} target="_blank" rel="noreferrer noopener" aria-label={`Открыть ссылку: ${i.name}`}><Icon name="link" size={16} /></a> : <span />}
                <span className="wish-price">{i.price !== null ? money(i.price, i.currency) : <span className="muted">—</span>}</span>
              </li>
            ))}
          </ul>
        </section>
      ))}
      {!items.length ? <p className="empty">Здесь пусто.</p> : null}
      {edit ? <WishModal item={edit === 'new' ? undefined : edit} onClose={() => setEdit(null)} /> : null}
    </div>
  );
}

function WishModal({ item, onClose }: { item?: WishlistItem; onClose: () => void }) {
  const [f, setF] = useState({
    name: item?.name ?? '', price: item?.price ?? null as number | null, priority: item?.priority ?? 3,
    url: item?.url ?? '', pros: item?.pros ?? '', cons: item?.cons ?? '', status: item?.status ?? 'active' as WishStatus,
  });
  const set = (p: Partial<typeof f>) => setF((x) => ({ ...x, ...p }));
  const { busy, run } = useAction();
  const save = () => run(async () => {
    const body = { ...f, currency: item?.currency ?? 'RUB' };
    if (item) await patch(`/wishlist/${item.id}`, body); else await post('/wishlist', body);
    invalidate('/wishlist');
    onClose();
  }, 'Сохранено');
  return (
    <Modal className="modal-form" title={item ? item.name : 'Новая покупка'} onClose={onClose} footer={<>
      {item ? <ConfirmButton size="md" onConfirm={async () => { await del(`/wishlist/${item.id}`); invalidate('/wishlist'); onClose(); }}>Удалить</ConfirmButton> : <span />}
      <div className="row-gap"><Button onClick={onClose}>Отмена</Button><Button variant="primary" busy={busy} onClick={save}>Сохранить</Button></div>
    </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        <Field label="Название"><Input value={f.name} onChange={(e) => set({ name: e.target.value })} autoFocus required /></Field>
        <div className="form-row">
          <Field label="Цена" className="grow"><NumberInput value={f.price} onChange={(v) => set({ price: v })} /></Field>
          <Field label="Приоритет" className="grow">
            <Select value={f.priority} onChange={(e) => set({ priority: Number(e.target.value) })}>
              {[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>{p}. {priorityLabels[p]}</option>)}
            </Select>
          </Field>
        </div>
        <Field label="Ссылка"><Input value={f.url} onChange={(e) => set({ url: e.target.value })} placeholder="https://" /></Field>
        <Field label="За"><textarea className={cx('input')} rows={2} value={f.pros} onChange={(e) => set({ pros: e.target.value })} /></Field>
        <Field label="Против"><textarea className="input" rows={2} value={f.cons} onChange={(e) => set({ cons: e.target.value })} /></Field>
        <Field label="Статус">
          <Select value={f.status} onChange={(e) => set({ status: e.target.value as WishStatus })}>
            <option value="active">В планах</option><option value="bought">Куплено</option><option value="dropped">Передумал</option>
          </Select>
        </Field>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
